import React, { useState, useEffect } from "react"
import { useOutletContext } from "react-router-dom"

//components
import { PrimeButton, SegmentedButtons } from "components-design-system"
import PdfFullWindowModal from "containers/files/components/PdfFullWindowModal/PdfFullWindowModal"
import DocumentsGrid from "./subcomponents/DocumentsGrid/DocumentsGrid"
import DocumentCard from "components/DocumentCard/DocumentCard"
import DocumentChecklists from "./subcomponents/DocumentChecklists/DocumentChecklists"
import FileUpload from "components/files/FileUpload/FileUpload"
import { Panel } from "components-design-system"

// context
import useStudy from "context/hooks/useStudy"
import useDocuments from "context/hooks/useDocuments"
import useChecklists from "context/hooks/useChecklists"

// utils
import useFileLoader from "utilities/useFileLoader"

// apis
import { useUserPermissions } from "api/hooks"

import "./Documents.scss"

const Documents = () => {
  const { newFileForm } = useOutletContext()

  const { openPanel, onOpenPanel, onClosePanel } = useStudy()

  const {
    openPdf,
    setOpenPdf,
    documents,
    selectedDocument,
    setSelectedDocument,
    onDropDocument,
    setDropFile,
    resetDocumentFormData,
    onOpenPdf,
  } = useDocuments()

  const { studyChecklists, setSelectedChecklist, setSelectedOption } = useChecklists()

  const [isDragging, setIsDraggin] = React.useState()

  const handleDropFile = (file) => {
    newFileForm.setFieldValue("document_file", file)
    onDropDocument(file)
  }

  const { getFileLoadAreaProps } = useFileLoader({
    onLoad: handleDropFile,
    onDrag: () => setIsDraggin(true),
    onStopDrag: () => setIsDraggin(false),
  })

  const { data: permissions } = useUserPermissions()

  const [viewType, setViewType] = useState("list")
  const [currView, setCurrView] = useState("files")
  const [gridApi, setGridApi] = useState(null)

  const nav = [
    {
      value: "files",
      label: "Files",
    },
    {
      value: "checklists",
      label: "Checklists",
    },
  ]

  useEffect(() => {
    onClosePanel()
  }, [currView])

  useEffect(() => {
    if (openPanel === false) {
      setSelectedDocument(null)
      setSelectedChecklist(null)
      setSelectedOption(null)
      resetDocumentFormData()
    }
  }, [openPanel])

  // called from document row
  const openRowPanel = (rowData) => {
    onOpenPanel("document-details")
    setSelectedDocument(rowData)
  }

  const showAddChecklistBtn = () => {
    return (
      permissions?.["study.checklist.create"] &&
      currView === "checklists" &&
      (documents?.length !== 0 || studyChecklists?.length !== 0)
    )
  }

  const openAddDocument = () => {
    setSelectedDocument(null)
    setDropFile(null)
    newFileForm.resetFields()
    onOpenPanel("new-file")
  }

  const openAddChecklist = () => {
    onOpenPanel("add-checklist")
  }

  const onOpenCardPreview = (document) => {
    if (document.document_file?.toLowerCase().includes(".pdf")) {
      onOpenPdf(document)
    } else {
      const file = document.document_link || document.document_file
      return window.open(file, "_blank")
    }
  }

  let height = documents?.length * 63 + 40

  if ((documents?.length === 0 || !documents) && height) {
    height += 63
  }

  const actions = React.useMemo(() => {
    const items = []

    if (currView === "files") {
      items.push(
        <PrimeButton
          icon={<i className={`fas ${viewType === "list" ? "fa-th-large" : "fa-th-list"}`} />}
          onClick={() => setViewType(viewType === "list" ? "grid" : "list")}
        />
      )
    }

    if (permissions?.["document.create"] === true) {
      items.push(
        <PrimeButton
          type="add"
          onClick={openAddDocument}
        >
          Add file
        </PrimeButton>
      )
    }

    if (showAddChecklistBtn()) {
      items.push(
        <PrimeButton
          type="add"
          onClick={openAddChecklist}
        >
          Add checklist
        </PrimeButton>
      )
    }
    return items
  }, [permissions, currView, viewType, showAddChecklistBtn()])

  return (
    <div
      className="documents-container"
      id="documents-container"
    >
      <div className="header-container">
        <SegmentedButtons
          options={nav}
          onChange={(val) => {
            setCurrView(val)
          }}
        />
      </div>
      <Panel
        title={currView === "checklists" ? "CHECKLISTS" : "FILES"}
        iconClassName={currView === "checklists" ? "fa-light fa-ballot-check" : "fal fa-file-alt"}
        className="main-container"
        actions={actions}
      >
        {currView === "files" && viewType === "list" && (
          <div className="docs-content-container">
            <DocumentsGrid
              documents={documents}
              gridApi={gridApi}
              setGridApi={setGridApi}
              openRowPanel={openRowPanel}
              openPdf={() => {}}
            />
            {permissions?.["document.edit"] && (
              <div {...getFileLoadAreaProps({ classNames: ["file-upload-container"] })}>
                <FileUpload
                  onLoad={handleDropFile}
                  isDragging={isDragging}
                />
              </div>
            )}
          </div>
        )}
        {currView === "files" && viewType === "grid" && (
          <div className="document-cards-container">
            {documents?.map((doc) => {
              return (
                <DocumentCard
                  key={doc.id}
                  document={doc}
                  onOpen={openRowPanel}
                  onOpenPreview={onOpenCardPreview}
                />
              )
            })}
          </div>
        )}
        {currView === "checklists" && (
          <DocumentChecklists
            onDropDocument={onDropDocument}
            openRowPanel={openRowPanel}
          />
        )}
      </Panel>

      <PdfFullWindowModal
        open={openPdf}
        setOpen={setOpenPdf}
        file={selectedDocument}
        setFile={setSelectedDocument}
        getContainer={() => document.getElementById("documents-container")}
      />
    </div>
  )
}

export default Documents
